import { useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import {
  buildGrowthSeries,
  projectValue,
  formatMoney,
  currencyForCountry,
} from "../lib/finance";
import {
  AreaChart,
  Area,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Slider } from "../components/ui/slider";
import { Button } from "../components/ui/button";
import { Calculator, Sparkles, TrendingUp, Coins } from "lucide-react";
import SpeakButton from "../components/SpeakButton";

const PRESETS = [
  { key: "prudent", rate: 0.03, color: "#A762B1" },
  { key: "equilibre", rate: 0.05, color: "#A762B1" },
  { key: "dynamique", rate: 0.07, color: "#A762B1" },
];

const T = {
  fr: {
    title: "Simulateur d'épargne",
    subtitle:
      "Bougez les curseurs et voyez comment votre argent grandit. Aucun engagement, juste pour comprendre.",
    monthly_label: "Combien mettre de côté chaque mois ?",
    duration_label: "Pendant combien d'années ?",
    rate_label: "À quel rendement annuel ?",
    presets_title: "Ou choisissez un profil",
    compare_on: "Comparer les 3 profils",
    compare_off: "Voir un seul profil",
    compare_help:
      "Les 3 courbes montrent ce que donneraient les 3 profils avec le même montant/durée. Le mauve monte le plus, mais peut baisser en cours de route.",
    presets: {
      prudent: "Prudent (3 %/an) — livret garanti",
      equilibre: "Équilibré (5 %/an) — mix sécurisé + Bourse",
      dynamique: "Dynamique (7 %/an) — Bourse à long terme",
    },
    invested_label: "Vous aurez mis de côté",
    final_label: "Vous aurez au total",
    gained_label: "Vous aurez gagné",
    gained_desc:
      "C'est ce que les intérêts vous rapportent, sans effort supplémentaire.",
    tip_title: "L'effet magique",
    tip_body:
      "Plus vous laissez le temps agir, plus la courbe monte vite. C'est ce qu'on appelle les « intérêts composés » : chaque année, vous gagnez des intérêts sur vos intérêts précédents.",
    min_title: "Pour commencer, très peu suffit",
    min_livret: "Livret A : dès 10 €",
    min_av: "Assurance-vie : dès 100 €",
    min_etf: "ETF : dès 25 €/mois",
    min_desc:
      "Ce qui compte, ce n'est pas la somme de départ. C'est de commencer, puis de tenir dans la durée. Vous pouvez démarrer petit et augmenter plus tard.",
    years: "ans",
    year: "an",
    months_per_year: "€/mois",
    per_year_return: "% par an",
    per_month_amount: "€/mois",
    listen_intro:
      "Voici un simulateur pour comprendre comment votre argent grandit. Bougez les curseurs pour voir combien vous aurez dans quelques années.",
  },
  en: {
    title: "Savings simulator",
    subtitle:
      "Move the sliders and see how your money grows. No commitment, just to understand.",
    monthly_label: "How much to set aside each month?",
    duration_label: "For how many years?",
    rate_label: "At what annual return?",
    presets_title: "Or pick a profile",
    compare_on: "Compare the 3 profiles",
    compare_off: "Show only one profile",
    compare_help:
      "The 3 curves show what each profile would give with the same amount and duration. Purple grows the most, but can dip along the way.",
    presets: {
      prudent: "Cautious (3%/y) — guaranteed savings account",
      equilibre: "Balanced (5%/y) — mix of safe + stocks",
      dynamique: "Dynamic (7%/y) — long-term stocks",
    },
    invested_label: "You'll have set aside",
    final_label: "You'll have in total",
    gained_label: "You'll have earned",
    gained_desc: "That's what interest brings you, with no extra effort.",
    tip_title: "The magic effect",
    tip_body:
      "The longer you let time do its work, the faster the curve rises. That's what we call \"compound interest\": each year, you earn interest on your previous interest.",
    min_title: "To get started, very little is enough",
    min_livret: "Livret A: from €10",
    min_av: "Life insurance: from €100",
    min_etf: "ETF: from €25/month",
    min_desc:
      "What matters isn't the starting amount. It's starting, then sticking with it over time. You can start small and increase later.",
    years: "years",
    year: "year",
    months_per_year: "/month",
    per_year_return: "% per year",
    per_month_amount: "/month",
    listen_intro:
      "This is a simulator to understand how your money grows. Move the sliders to see how much you will have in a few years.",
  },
};

export default function Simulator() {
  const { lang } = useLang();
  const { user } = useAuth();
  const tr = T[lang] || T.fr;
  const currency = currencyForCountry(user?.country || "FR");

  const [monthly, setMonthly] = useState(100);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(0.05);
  const [compareMode, setCompareMode] = useState(false);

  const series = useMemo(() => {
    const invested = (y) => monthly * 12 * y;
    if (compareMode) {
      // Build a combined series with all 3 preset rates on the same time axis
      const out = [];
      for (let y = 0; y <= years; y += 1) {
        out.push({
          year: y,
          prudent: Math.round(
            buildGrowthSeries({
              initial: 0,
              monthly,
              years: y,
              annualRate: PRESETS[0].rate,
            }).at(-1)?.value || 0
          ),
          equilibre: Math.round(
            buildGrowthSeries({
              initial: 0,
              monthly,
              years: y,
              annualRate: PRESETS[1].rate,
            }).at(-1)?.value || 0
          ),
          dynamique: Math.round(
            buildGrowthSeries({
              initial: 0,
              monthly,
              years: y,
              annualRate: PRESETS[2].rate,
            }).at(-1)?.value || 0
          ),
          invested: invested(y),
        });
      }
      return out;
    }
    return buildGrowthSeries({
      initial: 0,
      monthly,
      years,
      annualRate: rate,
    }).map((p) => ({
      year: p.year,
      value: p.value,
      invested: invested(p.year),
    }));
  }, [monthly, years, rate, compareMode]);

  const finalValue = projectValue({
    initial: 0,
    monthly,
    years,
    annualRate: rate,
  });
  const invested = monthly * 12 * years;
  const gained = Math.max(0, finalValue - invested);

  const listenText = `${tr.listen_intro} ${
    lang === "fr"
      ? `Avec ${monthly} euros par mois pendant ${years} ans à ${(rate * 100).toFixed(1)} pour cent par an, vous aurez environ ${formatMoney(finalValue, currency, lang)}. Vous aurez versé ${formatMoney(invested, currency, lang)} et gagné ${formatMoney(gained, currency, lang)} grâce aux intérêts.`
      : `With ${monthly} per month for ${years} years at ${(rate * 100).toFixed(1)} percent per year, you'll have about ${formatMoney(finalValue, currency, lang)}. You'll have contributed ${formatMoney(invested, currency, lang)} and earned ${formatMoney(gained, currency, lang)} from interest.`
  }`;

  return (
    <div
      className="mx-auto max-w-5xl px-6 py-12"
      data-testid="simulator-page"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#101A42]/10 px-3 py-1.5 text-sm font-semibold uppercase tracking-widest text-[#101A42]">
            <Calculator className="h-4 w-4" />
            {tr.title}
          </div>
          <h1 className="mt-4 font-serif text-4xl font-semibold text-[#101A42] sm:text-5xl">
            {tr.title}
          </h1>
          <p className="mt-3 text-lg leading-relaxed text-[#4A4A4A]">
            {tr.subtitle}
          </p>
        </div>
        <SpeakButton text={listenText} testid="simulator-listen" />
      </div>

      {/* Big numbers */}
      <div
        className="mt-10 grid gap-4 sm:grid-cols-3"
        data-testid="simulator-numbers"
      >
        <StatCard
          icon={Coins}
          label={tr.invested_label}
          value={formatMoney(invested, currency, lang)}
          testid="stat-invested"
        />
        <StatCard
          icon={TrendingUp}
          label={tr.final_label}
          value={formatMoney(finalValue, currency, lang)}
          accent
          testid="stat-final"
        />
        <StatCard
          icon={Sparkles}
          label={tr.gained_label}
          value={formatMoney(gained, currency, lang)}
          gold
          testid="stat-gained"
        />
      </div>

      {/* Minimum to start — informative */}
      <div
        className="mt-6 rounded-2xl border-2 border-[#E59D32]/40 bg-gradient-to-br from-[#FFF7E9] to-[#FFF7E9] p-5 sm:p-6"
        data-testid="simulator-minimum"
      >
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#B8860B]">
          <Coins className="h-4 w-4" />
          {tr.min_title}
        </div>
        <p className="text-base leading-relaxed text-[#101A42]">
          <strong>{tr.min_livret}</strong> • <strong>{tr.min_av}</strong> • <strong>{tr.min_etf}</strong>
        </p>
        <p className="mt-2 text-sm text-[#4A4A4A]">{tr.min_desc}</p>
      </div>

      {/* Chart */}
      <div
        className="mt-8 rounded-3xl border-2 border-[#E8DFC7] bg-white p-4 sm:p-6"
        data-testid="simulator-chart"
      >
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={series}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="sim_growth" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#101A42" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#101A42" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="sim_invested" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#A762B1" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#A762B1" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#E8DFC7" vertical={false} />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 14, fill: "#4A4A4A" }}
              tickFormatter={(y) =>
                `${y} ${y > 1 ? tr.years : y === 1 ? tr.year : tr.years}`
              }
            />
            <YAxis
              tick={{ fontSize: 14, fill: "#4A4A4A" }}
              tickFormatter={(v) => formatMoney(v, currency, lang)}
              width={90}
            />
            <Tooltip
              formatter={(v, name) => [
                formatMoney(v, currency, lang),
                name === "value" ? tr.final_label : tr.invested_label,
              ]}
              labelFormatter={(y) =>
                `${y} ${y > 1 ? tr.years : y === 1 ? tr.year : tr.years}`
              }
              contentStyle={{
                background: "#fff",
                border: "2px solid #101A42",
                borderRadius: 12,
                fontSize: 14,
              }}
            />
            <Area
              type="monotone"
              dataKey="invested"
              stroke="#A762B1"
              strokeWidth={2}
              strokeDasharray="4 4"
              fill="url(#sim_invested)"
            />
            {compareMode ? (
              <>
                <Area
                  type="monotone"
                  dataKey="prudent"
                  stroke="#A762B1"
                  strokeWidth={3}
                  fill="none"
                />
                <Area
                  type="monotone"
                  dataKey="equilibre"
                  stroke="#A762B1"
                  strokeWidth={3}
                  fill="none"
                />
                <Area
                  type="monotone"
                  dataKey="dynamique"
                  stroke="#A762B1"
                  strokeWidth={3}
                  fill="none"
                />
              </>
            ) : (
              <Area
                type="monotone"
                dataKey="value"
                stroke="#101A42"
                strokeWidth={4}
                fill="url(#sim_growth)"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
        {compareMode ? (
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-[#4A4A4A]">
            <span className="inline-flex items-center gap-2">
              <span className="inline-block h-1 w-6 rounded-full bg-[#A762B1]" />
              {tr.presets.prudent}
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-block h-1 w-6 rounded-full bg-[#A762B1]" />
              {tr.presets.equilibre}
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-block h-1 w-6 rounded-full bg-[#A762B1]" />
              {tr.presets.dynamique}
            </span>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-[#4A4A4A]">
            <span className="inline-flex items-center gap-2">
              <span className="inline-block h-1 w-6 rounded-full bg-[#101A42]" />
              {tr.final_label}
            </span>
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-block h-0.5 w-6 rounded-full"
                style={{
                  background:
                    "repeating-linear-gradient(to right, #A762B1, #A762B1 4px, transparent 4px, transparent 8px)",
                }}
              />
              {tr.invested_label}
            </span>
          </div>
        )}
      </div>

      {/* Compare mode toggle */}
      <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="compare-toggle-row">
        <button
          type="button"
          onClick={() => setCompareMode((c) => !c)}
          data-testid="compare-toggle-btn"
          className={`min-h-[48px] rounded-full border-2 px-5 text-base font-semibold transition-colors ${
            compareMode
              ? "border-[#A762B1] bg-[#A762B1] text-white shadow"
              : "border-[#A762B1]/40 bg-white text-[#8E4799] hover:bg-[#A762B1]/10"
          }`}
        >
          {compareMode ? tr.compare_off : tr.compare_on}
        </button>
        {compareMode && (
          <p className="text-sm text-[#4A4A4A]" data-testid="compare-help">
            {tr.compare_help}
          </p>
        )}
      </div>

      {/* Sliders */}
      <div
        className="mt-8 grid gap-6 rounded-3xl border-2 border-[#E8DFC7] bg-white p-6 sm:p-8"
        data-testid="simulator-sliders"
      >
        <SliderRow
          label={tr.monthly_label}
          value={`${formatMoney(monthly, currency, lang)}${lang === "fr" ? "/mois" : "/mo"}`}
          testid="slider-monthly"
        >
          <Slider
            min={20}
            max={1000}
            step={10}
            value={[monthly]}
            onValueChange={(v) => setMonthly(v[0])}
            className="h-2"
          />
          <div className="mt-1 flex justify-between text-xs text-[#8A8A8A]">
            <span>{formatMoney(20, currency, lang)}</span>
            <span>{formatMoney(1000, currency, lang)}</span>
          </div>
        </SliderRow>

        <SliderRow
          label={tr.duration_label}
          value={`${years} ${years > 1 ? tr.years : tr.year}`}
          testid="slider-years"
        >
          <Slider
            min={1}
            max={25}
            step={1}
            value={[years]}
            onValueChange={(v) => setYears(v[0])}
            className="h-2"
          />
          <div className="mt-1 flex justify-between text-xs text-[#8A8A8A]">
            <span>1 {tr.year}</span>
            <span>25 {tr.years}</span>
          </div>
        </SliderRow>

        <SliderRow
          label={tr.rate_label}
          value={`${(rate * 100).toFixed(1)}${tr.per_year_return}`}
          testid="slider-rate"
        >
          <Slider
            min={0.01}
            max={0.09}
            step={0.005}
            value={[rate]}
            onValueChange={(v) => setRate(v[0])}
            className="h-2"
          />
          <div className="mt-1 flex justify-between text-xs text-[#8A8A8A]">
            <span>1 %</span>
            <span>9 %</span>
          </div>
        </SliderRow>
      </div>

      {/* Presets */}
      <div className="mt-8" data-testid="simulator-presets">
        <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-[#4A4A4A]">
          {tr.presets_title}
        </p>
        <div className="flex flex-wrap gap-3">
          {PRESETS.map((p) => {
            const active = Math.abs(rate - p.rate) < 0.0025;
            return (
              <Button
                key={p.key}
                type="button"
                variant="outline"
                onClick={() => setRate(p.rate)}
                data-testid={`preset-${p.key}${active ? "-active" : ""}`}
                className={`min-h-[52px] rounded-full border-2 px-5 text-base font-semibold transition-colors ${
                  active
                    ? "border-transparent text-white shadow"
                    : "border-[#101A42]/25 bg-white text-[#101A42] hover:border-[#101A42]"
                }`}
                style={
                  active
                    ? { background: p.color, borderColor: p.color }
                    : undefined
                }
              >
                {tr.presets[p.key]}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Tip */}
      <div
        className="mt-8 rounded-3xl border-2 border-[#A762B1]/30 bg-gradient-to-br from-[#F5F0FF] via-[#FFF7E9] to-[#FFF7E9] p-6 sm:p-8"
        data-testid="simulator-tip"
      >
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#8E4799]">
          <Sparkles className="h-4 w-4" />
          {tr.tip_title}
        </div>
        <p className="text-lg leading-relaxed text-[#101A42]">{tr.tip_body}</p>
        <p className="mt-3 text-base text-[#4A4A4A]">{tr.gained_desc}</p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, gold, testid }) {
  const styles = gold
    ? "border-[#E59D32]/60 bg-gradient-to-br from-[#FFF3C4] to-[#FFE68C] text-[#5B4200]"
    : accent
    ? "border-[#101A42] bg-[#101A42] text-white"
    : "border-[#E8DFC7] bg-white";
  const labelColor = accent
    ? "text-[#FFF7E9]/80"
    : gold
    ? "text-[#8A6300]"
    : "text-[#4A4A4A]";
  const valueColor = accent
    ? "text-white"
    : gold
    ? "text-[#5B4200]"
    : "text-[#101A42]";
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border-2 p-5 transition-transform ${styles} ${
        gold ? "shadow-[0_8px_24px_rgba(212,160,23,0.25)]" : ""
      }`}
    >
      <div
        className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-widest ${labelColor}`}
      >
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <p
        className={`mt-2 font-serif text-3xl font-semibold sm:text-4xl ${valueColor}`}
      >
        {value}
      </p>
    </div>
  );
}

function SliderRow({ label, value, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-lg font-medium text-[#101A42]">{label}</span>
        <span
          className="font-serif text-2xl font-semibold text-[#101A42]"
          data-testid={`${testid}-value`}
        >
          {value}
        </span>
      </div>
      {children}
    </div>
  );
}
